import { RepairRequest, RepairStatus } from '../types/firestore';
import {
  getRepairRequestIdentificationPresentation,
  getRepairRequestPresentation,
} from './repairRequestPresentation';
import { getRepairRequestUnreadState } from './repairRequestReadModel';
import {
  getRepairStatusMatrix,
  getRepairRequestOrientation,
  type RepairRequestWorkflowAction,
  type RepairRequestWorkflowActor,
  type RepairRequestOrientation,
} from './repairRequestWorkflow';
import { resolveRepairWorkflowReadModel } from './repairWorkflowV15';
import {
  resolveRepairRequestFulfillmentSnapshot,
  type FulfillmentMode,
} from '../shared/fulfillment';

export type OrderCardPanelId = 'description' | 'communication' | 'settlement';
export type OrderCardRuntimeVariant = 'detail' | 'expanded';
export type OrderCardBadgeVariant =
  | 'primary'
  | 'neutral'
  | 'accent'
  | 'error'
  | 'success'
  | 'warning'
  | 'info';
export type OrderCardNextActionType =
  | 'workflow_transition'
  | 'quote_submission'
  | 'quote_decision'
  | 'chat_message'
  | 'none';
export type OrderCardUnreadSection = 'messages' | 'quote' | 'status';
export type OrderCardSignalTone = 'danger' | 'warning' | 'success' | 'neutral';

export interface OrderCardHeaderBadge {
  id: string;
  label: string;
  variant: OrderCardBadgeVariant;
}

export interface OrderCardAvailableActions {
  primary: RepairRequestWorkflowAction | null;
  secondary: RepairRequestWorkflowAction[];
  destructive: RepairRequestWorkflowAction[];
  all: RepairRequestWorkflowAction[];
}

export interface OrderCardState {
  status: RepairStatus;
  stage: ReturnType<typeof getRepairStatusMatrix>['serviceDashboardBucket'];
  stageLabel: string;
  nextActionOwner: ReturnType<typeof getRepairStatusMatrix>['nextActionOwner'];
  orientation: RepairRequestOrientation;
  nextActionType: OrderCardNextActionType;
  hasWorkflowAction: boolean;
  hasUnreadSignal: boolean;
  showActionUrgency: boolean;
  requiresAttention: boolean;
  attentionReason: string | null;
  primaryPanel: OrderCardPanelId;
  actionPanel: Exclude<OrderCardPanelId, 'communication'>;
  orderTone: OrderCardSignalTone;
  availableActions: OrderCardAvailableActions;
  unreadSections: OrderCardUnreadSection[];
  headerBadges: OrderCardHeaderBadge[];
  summaryLine: string;
  actor: RepairRequestWorkflowActor;
  variant: OrderCardRuntimeVariant;
  communicationPriority: ReturnType<typeof getRepairRequestPresentation>['communicationPriority'];
  hasUnreadMessages: boolean;
  hasFreshRequestActivity: boolean;
  header: {
    title: string;
    brandModelLabel: string;
    requestShortId: string;
    counterpartLabel: string;
    statusLabel: string;
    stageLabel: string;
    issueSummary: string;
    identification: ReturnType<typeof getRepairRequestIdentificationPresentation>;
  };
  rows: {
    description: {
      title: string;
      subtitle: string;
      tone: OrderCardSignalTone;
      hasAttention: boolean;
      section: string;
      actionCount: number;
    };
    communication: {
      title: string;
      subtitle: string;
      tone: OrderCardSignalTone;
      hasAttention: boolean;
      section: string;
      hint: string;
    };
    settlement: {
      title: string;
      subtitle: string;
      tone: OrderCardSignalTone;
      hasAttention: boolean;
      section: string;
      actionCount: number;
    };
  };
}

export interface BuildOrderCardStateInput {
  request: RepairRequest;
  actor: RepairRequestWorkflowActor;
  variant?: OrderCardRuntimeVariant;
  sectionHint?: string | null;
  counterpartLabel: string;
  showReview: boolean;
}

export interface OrderCardRoleAwareCurrentStep {
  label: string;
  description: string;
  noActionDescription: string;
}

type RoleAwareStepCopy = Record<RepairRequestWorkflowActor, OrderCardRoleAwareCurrentStep>;

const getAwaitingFulfillmentCurrentStepCopy = (
  mode: FulfillmentMode | undefined,
  actor: RepairRequestWorkflowActor
): OrderCardRoleAwareCurrentStep => {
  if (mode === 'customerDropoff') {
    return actor === 'service'
      ? {
          label: 'PRZYJĘCIE SPRZĘTU',
          description:
            'Klient powinien dostarczyć sprzęt do serwisu. Po przyjęciu potwierdź przekazanie w systemie.',
          noActionDescription:
            'Czekamy na dostarczenie sprzętu; po przyjęciu potwierdź przekazanie w systemie.',
        }
      : {
          label: 'CO TERAZ',
          description:
            'Dostarcz sprzęt do serwisu zgodnie z ustaleniami. Po przyjęciu serwis potwierdzi przekazanie w systemie.',
          noActionDescription:
            'Czekamy na dostarczenie sprzętu; serwis potwierdzi przekazanie po przyjęciu.',
        };
  }

  if (mode === 'servicePickup') {
    return actor === 'service'
      ? {
          label: 'ODBIÓR OD KLIENTA',
          description:
            'Umów odbiór sprzętu od klienta. Po odbiorze potwierdź przekazanie w systemie.',
          noActionDescription:
            'Ustal odbiór sprzętu z klientem, a po odbiorze potwierdź przekazanie.',
        }
      : {
          label: 'ODBIÓR OD KLIENTA',
          description:
            'Serwis skontaktuje się, aby ustalić odbiór sprzętu spod wskazanego adresu, a po odbiorze potwierdzi przekazanie w systemie.',
          noActionDescription:
            'Czekamy na ustalenie odbioru sprzętu; serwis potwierdzi przekazanie po odbiorze.',
        };
  }

  if (mode === 'onsiteService') {
    return actor === 'service'
      ? {
          label: 'WIZYTA U KLIENTA',
          description:
            'Umów wizytę u klienta. Po rozpoczęciu wizyty potwierdź przekazanie w systemie.',
          noActionDescription:
            'Ustal wizytę z klientem, a po jej rozpoczęciu potwierdź przekazanie.',
        }
      : {
          label: 'WIZYTA U KLIENTA',
          description:
            'Serwis skontaktuje się, aby ustalić wizytę pod wskazanym adresem klienta, a po jej rozpoczęciu potwierdzi przekazanie w systemie.',
          noActionDescription:
            'Czekamy na ustalenie wizyty; serwis potwierdzi przekazanie po jej rozpoczęciu.',
        };
  }

  return actor === 'service'
    ? {
        label: 'REALIZACJA',
        description: 'Ustal z klientem sposób przekazania sprzętu lub wizyty.',
        noActionDescription: 'Czekamy na ustalenie realizacji.',
      }
    : {
        label: 'AKTUALNY KROK',
        description: 'Serwis skontaktuje się w sprawie kolejnego kroku.',
        noActionDescription: 'Czekamy na aktualizację sprawy.',
      };
};

const V2_ROLE_AWARE_CURRENT_STEP_COPY: Partial<Record<RepairStatus, RoleAwareStepCopy>> = {
  [RepairStatus.PRELIMINARY_QUOTED]: {
    service: {
      label: 'CZEKA NA KLIENTA',
      description: 'Wycena wstępna została wysłana. Klient musi ją zaakceptować albo odrzucić.',
      noActionDescription: 'Czekamy na decyzję klienta.',
    },
    client: {
      label: 'TWOJA DECYZJA',
      description: 'Serwis wysłał wycenę wstępną. Zaakceptuj rozpoczęcie procesu albo odrzuć.',
      noActionDescription: 'Czekamy na decyzję klienta.',
    },
  },
  [RepairStatus.DIAGNOSIS_IN_PROGRESS]: {
    service: {
      label: 'DIAGNOZA I WYCENA',
      description: 'Uzupełnij diagnozę i kosztorys finalnej naprawy.',
      noActionDescription: 'Czekamy na finalną wycenę.',
    },
    client: {
      label: 'AKTUALNY KROK',
      description: 'Serwis prowadzi diagnozę. Po diagnozie otrzymasz finalną wycenę.',
      noActionDescription: 'Czekamy na finalną wycenę.',
    },
  },
  [RepairStatus.FINAL_QUOTE_READY]: {
    service: {
      label: 'CZEKA NA KLIENTA',
      description: 'Finalna wycena została wysłana. Klient musi ją zaakceptować albo odrzucić.',
      noActionDescription: 'Czekamy na decyzję klienta.',
    },
    client: {
      label: 'TWOJA DECYZJA',
      description:
        'Sprawdź diagnozę i kosztorys. Możesz zaakceptować albo odrzucić finalną wycenę.',
      noActionDescription: 'Czekamy na decyzję klienta.',
    },
  },
  [RepairStatus.FINAL_QUOTE_ACCEPTED]: {
    service: {
      label: 'ROZPOCZNIJ NAPRAWĘ',
      description: 'Klient zaakceptował finalną wycenę. Możesz rozpocząć naprawę.',
      noActionDescription: 'Czekamy na rozpoczęcie naprawy.',
    },
    client: {
      label: 'AKTUALNY KROK',
      description: 'Finalna wycena została zaakceptowana. Serwis może rozpocząć naprawę.',
      noActionDescription: 'Czekamy na rozpoczęcie naprawy.',
    },
  },
  [RepairStatus.REPAIR_IN_PROGRESS]: {
    service: {
      label: 'NAPRAWA W TOKU',
      description: 'Oznacz naprawę jako wykonaną po zakończeniu prac.',
      noActionDescription: 'Czekamy na zakończenie naprawy.',
    },
    client: {
      label: 'AKTUALNY KROK',
      description: 'Serwis prowadzi naprawę zgodnie z zaakceptowaną finalną wyceną.',
      noActionDescription: 'Czekamy na zakończenie naprawy.',
    },
  },
  [RepairStatus.REPAIR_COMPLETED]: {
    service: {
      label: 'PRZYGOTUJ ROZLICZENIE',
      description:
        'Naprawa została oznaczona jako wykonana. Przygotuj rozliczenie gotówką lub bezpośrednio.',
      noActionDescription: 'Czekamy na przygotowanie rozliczenia.',
    },
    client: {
      label: 'AKTUALNY KROK',
      description:
        'Naprawa została wykonana. Serwis powinien przygotować rozliczenie gotówką lub bezpośrednio.',
      noActionDescription: 'Czekamy na przygotowanie rozliczenia.',
    },
  },
  [RepairStatus.AWAITING_CASH_SETTLEMENT]: {
    service: {
      label: 'POTWIERDŹ ROZLICZENIE',
      description: 'Potwierdzenie zamknie zlecenie jako zakończone i rozliczone.',
      noActionDescription: 'Czekamy na potwierdzenie rozliczenia.',
    },
    client: {
      label: 'AKTUALNY KROK',
      description:
        'Naprawa czeka na rozliczenie gotówką lub bezpośrednio i potwierdzenie zakończenia.',
      noActionDescription: 'Czekamy na potwierdzenie rozliczenia.',
    },
  },
  [RepairStatus.COMPLETED]: {
    service: {
      label: 'ZAMKNIĘTE',
      description: 'Sprawa jest zamknięta. Rozliczenie zostało potwierdzone.',
      noActionDescription: 'Sprawa jest zamknięta.',
    },
    client: {
      label: 'ZAKOŃCZONE',
      description: 'Sprawa jest zamknięta.',
      noActionDescription: 'Sprawa jest zamknięta.',
    },
  },
  [RepairStatus.CANCELLED_BY_SERVICE]: {
    service: {
      label: 'ZAMKNIĘTE BEZ NAPRAWY',
      description: 'Sprawa została zamknięta przez serwis.',
      noActionDescription: 'Sprawa została zamknięta przez serwis.',
    },
    client: {
      label: 'SERWIS NIE PODEJMIE NAPRAWY',
      description: 'Serwis zamknął sprawę bez naprawy.',
      noActionDescription: 'Serwis zamknął sprawę bez naprawy.',
    },
  },
};

export const getOrderCardRoleAwareCurrentStep = (
  request: RepairRequest,
  actor: RepairRequestWorkflowActor
): OrderCardRoleAwareCurrentStep | null => {
  const workflowV15 = resolveRepairWorkflowReadModel(request);
  if (!workflowV15.isV15) {
    return null;
  }
  if (workflowV15.stage === 'degraded' || workflowV15.nextStep.kind === 'degraded') {
    return null;
  }
  if (request.status === RepairStatus.AWAITING_FULFILLMENT) {
    const fulfillment = resolveRepairRequestFulfillmentSnapshot(request);
    return getAwaitingFulfillmentCurrentStepCopy(fulfillment.selectedMode, actor);
  }
  return V2_ROLE_AWARE_CURRENT_STEP_COPY[request.status]?.[actor] || null;
};

export const getOrderCardNoActionStateMessage = (
  request: RepairRequest,
  actor: RepairRequestWorkflowActor
): string => {
  const roleAwareStep = getOrderCardRoleAwareCurrentStep(request, actor);
  if (roleAwareStep) {
    return roleAwareStep.noActionDescription;
  }

  const statusMatrix = getRepairStatusMatrix(request.status);
  if (statusMatrix.isTerminal) {
    return 'Sprawa jest zamknięta.';
  }
  return actor === 'service'
    ? 'Czekamy na kolejny ruch w sprawie.'
    : 'Czekamy na aktualizację sprawy.';
};

const COMMUNICATION_PANEL_SECTION_HINTS = new Set(['messages', 'message', 'chat', 'komunikacja']);
const DESCRIPTION_PANEL_SECTION_HINTS = new Set([
  'description',
  'opis',
  'intake',
  'data',
  'details',
  'dane',
  'szczegoly',
  'process',
  'workflow',
  'steps',
  'etapy',
]);
const SETTLEMENT_PANEL_SECTION_HINTS = new Set([
  'settlement',
  'billing',
  'payment',
  'quote',
  'review',
  'history',
  'historia',
  'rozliczenie',
  'zakonczenie',
]);
const ACTION_PANEL_SECTION_HINTS = new Set(['action', 'actions', 'cta', 'decision', 'status']);

const INTAKE_STATUSES = new Set<RepairStatus>([RepairStatus.NEW, RepairStatus.PENDING_QUOTE]);

const getPrimaryActionCue = (
  primaryAction: RepairRequestWorkflowAction | null,
  actor: RepairRequestWorkflowActor
): string | null => {
  if (!primaryAction) {
    return null;
  }

  switch (primaryAction.id) {
    case 'service_start_quote':
      return 'Przyjmij lub odrzuć';
    case 'service_send_quote':
      return 'Wyślij wycenę';
    case 'service_start_repair':
      return 'Rozpocznij naprawę';
    case 'service_mark_pending_payment':
    case 'service_prepare_cash_settlement':
      return 'Do płatności';
    case 'service_complete_directly':
    case 'service_complete_after_payment':
    case 'service_confirm_cash_settlement':
      return 'Do zamknięcia';
    case 'service_mark_v2_repair_completed':
      return 'Zakończ prace';
    case 'client_accept_quote':
    case 'client_reject_quote':
      return actor === 'client' ? 'Przyjmij lub odrzuć' : 'Czeka na decyzję właściciela zgłoszenia';
    default:
      return primaryAction.label;
  }
};

const getActionType = (
  primaryAction: RepairRequestWorkflowAction | null,
  hasUnreadMessages: boolean,
  status: RepairStatus,
  actor: RepairRequestWorkflowActor
): OrderCardNextActionType => {
  if (hasUnreadMessages) {
    return 'chat_message';
  }

  if (!primaryAction) {
    return 'none';
  }

  if (primaryAction.nextStatus === RepairStatus.QUOTED) {
    return 'quote_submission';
  }

  if (
    actor === 'client' &&
    status === RepairStatus.QUOTED &&
    (primaryAction.nextStatus === RepairStatus.ACCEPTED ||
      primaryAction.nextStatus === RepairStatus.REJECTED)
  ) {
    return 'quote_decision';
  }

  return 'workflow_transition';
};

const getAttentionReason = ({
  hasActionableAttention,
  primaryAction,
  actor,
}: {
  hasActionableAttention: boolean;
  primaryAction: RepairRequestWorkflowAction | null;
  actor: RepairRequestWorkflowActor;
}): string | null => {
  if (hasActionableAttention && primaryAction) {
    return getPrimaryActionCue(primaryAction, actor);
  }
  if (hasActionableAttention) {
    return actor === 'service' ? 'Wymaga działania' : 'Wymaga decyzji';
  }
  return null;
};

const getActionPanel = (request: RepairRequest): Exclude<OrderCardPanelId, 'communication'> => {
  const workflowV15 = resolveRepairWorkflowReadModel(request);
  if (workflowV15.isV15) {
    return 'settlement';
  }

  return INTAKE_STATUSES.has(request.status) ? 'description' : 'settlement';
};

const resolveSectionHint = (
  section: string | null | undefined,
  actionPanel: Exclude<OrderCardPanelId, 'communication'>
): OrderCardPanelId | null => {
  const normalized = String(section || '')
    .trim()
    .toLowerCase();
  if (!normalized) {
    return null;
  }
  if (COMMUNICATION_PANEL_SECTION_HINTS.has(normalized)) {
    return 'communication';
  }
  if (ACTION_PANEL_SECTION_HINTS.has(normalized)) {
    return actionPanel;
  }
  if (DESCRIPTION_PANEL_SECTION_HINTS.has(normalized)) {
    return 'description';
  }
  if (SETTLEMENT_PANEL_SECTION_HINTS.has(normalized)) {
    return 'settlement';
  }
  return null;
};

export const resolveOrderCardPanelFromSection = (
  section: string | null | undefined
): OrderCardPanelId | null => resolveSectionHint(section, 'settlement');

const getPrimaryPanel = ({
  panelFromSection,
  hasUnreadMessages,
  communicationPriority,
  hasActionableAttention,
  actionPanel,
  isTerminal,
}: {
  panelFromSection: OrderCardPanelId | null;
  hasUnreadMessages: boolean;
  communicationPriority: ReturnType<typeof getRepairRequestPresentation>['communicationPriority'];
  hasActionableAttention: boolean;
  actionPanel: Exclude<OrderCardPanelId, 'communication'>;
  isTerminal: boolean;
}): OrderCardPanelId => {
  if (panelFromSection) {
    return panelFromSection;
  }
  if (hasUnreadMessages || communicationPriority === 'urgent') {
    return 'communication';
  }
  if (hasActionableAttention) {
    return actionPanel;
  }
  return isTerminal ? 'settlement' : 'description';
};

const getOrderTone = ({
  status,
  matrixTone,
  showActionUrgency,
}: {
  status: RepairStatus;
  matrixTone: ReturnType<typeof getRepairStatusMatrix>['serviceDashboardTone'];
  showActionUrgency: boolean;
}): OrderCardSignalTone => {
  if (status === RepairStatus.COMPLETED) {
    return 'success';
  }
  if (
    status === RepairStatus.CANCELLED_BY_CLIENT ||
    status === RepairStatus.CANCELLED_BY_SERVICE ||
    status === RepairStatus.FINAL_QUOTE_REJECTED ||
    status === RepairStatus.PRELIMINARY_REJECTED ||
    status === RepairStatus.REJECTED
  ) {
    return 'neutral';
  }
  if (matrixTone === 'danger') {
    return 'danger';
  }
  if (showActionUrgency || matrixTone === 'warning') {
    return 'warning';
  }
  return 'neutral';
};

const getCommunicationSubtitle = ({
  communicationLabel,
}: {
  communicationLabel: string | null;
}): string => {
  return communicationLabel || 'Brak nowych wiadomości';
};

const getSettlementSubtitle = ({
  status,
  actor,
}: {
  status: RepairStatus;
  actor: RepairRequestWorkflowActor;
}): string => {
  switch (status) {
    case RepairStatus.QUOTED:
      return actor === 'client'
        ? 'Czeka na Twoją decyzję'
        : 'Czeka na decyzję właściciela zgłoszenia';
    case RepairStatus.ACCEPTED:
      return 'Wycena przyjęta';
    case RepairStatus.IN_PROGRESS:
      return 'Naprawa w toku';
    case RepairStatus.PENDING_PAYMENT:
      return 'Oczekuje płatności';
    case RepairStatus.FINAL_QUOTE_READY:
      return actor === 'client'
        ? 'Czeka na Twoją decyzję'
        : 'Czeka na decyzję właściciela zgłoszenia';
    case RepairStatus.FINAL_QUOTE_ACCEPTED:
      return 'Naprawa zaakceptowana';
    case RepairStatus.REPAIR_IN_PROGRESS:
      return 'Naprawa w toku';
    case RepairStatus.REPAIR_COMPLETED:
      return 'Naprawa wykonana';
    case RepairStatus.AWAITING_CASH_SETTLEMENT:
      return 'Oczekuje rozliczenia';
    case RepairStatus.FINAL_QUOTE_REJECTED:
      return 'Naprawa niezaakceptowana';
    case RepairStatus.CANCELLED_BY_SERVICE:
      return actor === 'client' ? 'Serwis nie podejmie naprawy' : 'Zamknięte bez naprawy';
    case RepairStatus.COMPLETED:
      return 'Rozliczone';
    case RepairStatus.REJECTED:
    case RepairStatus.PRELIMINARY_REJECTED:
    case RepairStatus.CANCELLED_BY_CLIENT:
      return 'Sprawa zamknięta';
    default:
      return 'Bez akcji rozliczeniowych';
  }
};

const SETTLEMENT_RUNTIME_TITLE_STATUSES = new Set<RepairStatus>([
  RepairStatus.REPAIR_COMPLETED,
  RepairStatus.AWAITING_CASH_SETTLEMENT,
  RepairStatus.PENDING_PAYMENT,
  RepairStatus.COMPLETED,
]);

const getSettlementTitle = (status: RepairStatus, isWorkflowV15: boolean): string => {
  if (SETTLEMENT_RUNTIME_TITLE_STATUSES.has(status)) {
    return 'Rozliczenie i zakończenie';
  }
  return 'Proces i decyzje';
};

export const buildOrderCardState = ({
  request,
  actor,
  variant = 'detail',
  sectionHint,
  counterpartLabel,
}: BuildOrderCardStateInput): OrderCardState => {
  const presentation = getRepairRequestPresentation(request, actor);
  const statusMatrix = getRepairStatusMatrix(request.status);
  const orientation = getRepairRequestOrientation(request.status, actor);
  const unreadState = getRepairRequestUnreadState(request, actor);
  const unreadSections = unreadState.sections as OrderCardUnreadSection[];
  const hasUnreadMessages = unreadState.hasUnreadMessages;
  const hasFreshRequestActivity = unreadState.hasFreshRequestActivity;
  const roleAwareCurrentStep = getOrderCardRoleAwareCurrentStep(request, actor);
  const allActions = [
    ...(presentation.primaryAction ? [presentation.primaryAction] : []),
    ...presentation.secondaryActions,
    ...presentation.destructiveActions,
  ];
  const workflowV15 = resolveRepairWorkflowReadModel(request);
  const hasV15ActionableAttention =
    workflowV15.isV15 &&
    ((actor === 'service' && workflowV15.nextStep.id === 'submit_preliminary_estimate') ||
      (actor === 'service' &&
        request.status === RepairStatus.AWAITING_FULFILLMENT &&
        (workflowV15.stage === 'awaiting_customer_delivery' ||
          workflowV15.stage === 'awaiting_service_pickup' ||
          workflowV15.stage === 'awaiting_onsite_visit')) ||
      (actor === 'service' && workflowV15.nextStep.id === 'submit_final_quote') ||
      (actor === 'service' && workflowV15.nextStep.id === 'start_repair_work') ||
      (actor === 'service' && workflowV15.nextStep.id === 'complete_repair_work') ||
      (actor === 'service' && workflowV15.nextStep.id === 'prepare_cash_settlement') ||
      (actor === 'service' && workflowV15.nextStep.id === 'confirm_cash_release') ||
      (actor === 'client' && workflowV15.nextStep.id === 'decide_preliminary_estimate') ||
      (actor === 'client' && workflowV15.nextStep.id === 'decide_final_quote'));
  const hasActionableAttention =
    (presentation.requiresReaction && allActions.length > 0) || hasV15ActionableAttention;
  const actionPanel = getActionPanel(request);
  const panelFromSection = resolveSectionHint(sectionHint, actionPanel);
  const workflowV15StagePanel =
    workflowV15.isV15 &&
    (workflowV15.stage === 'awaiting_customer_delivery' ||
      workflowV15.stage === 'awaiting_service_pickup' ||
      workflowV15.stage === 'awaiting_onsite_visit' ||
      workflowV15.stage === 'awaiting_fulfillment_confirmation' ||
      workflowV15.stage === 'diagnosis_in_progress' ||
      workflowV15.stage === 'final_quote_decision' ||
      workflowV15.stage === 'repair_ready' ||
      workflowV15.stage === 'repair_in_progress' ||
      workflowV15.stage === 'cash_settlement_pending' ||
      workflowV15.stage === 'rejected' ||
      workflowV15.stage === 'degraded')
      ? 'settlement'
      : null;
  const primaryPanel =
    workflowV15StagePanel ||
    getPrimaryPanel({
      panelFromSection,
      hasUnreadMessages,
      communicationPriority: presentation.communicationPriority,
      hasActionableAttention,
      actionPanel,
      isTerminal: statusMatrix.isTerminal,
    });
  const actionCue =
    roleAwareCurrentStep?.description ||
    (hasV15ActionableAttention
      ? workflowV15.nextStep.label
      : getPrimaryActionCue(presentation.primaryAction, actor));
  const summaryLine = actionCue || orientation.nextStep;
  const attentionReason = getAttentionReason({
    hasActionableAttention,
    primaryAction: presentation.primaryAction,
    actor,
  });
  const hasWorkflowAction = hasActionableAttention;
  const hasUnreadSignal = unreadSections.length > 0;
  const showActionUrgency = hasWorkflowAction;
  const requiresAttention = hasWorkflowAction || hasUnreadSignal;
  const identification = getRepairRequestIdentificationPresentation(request, 120);
  const device = identification.device;
  const issueSummary = identification.problemSummary;
  const orderTone = getOrderTone({
    status: request.status,
    matrixTone: statusMatrix.serviceDashboardTone,
    showActionUrgency,
  });
  const actionCount = allActions.length + (hasV15ActionableAttention ? 1 : 0);
  const descriptionActionCount = actionPanel === 'description' ? actionCount : 0;
  const settlementActionCount = actionPanel === 'settlement' ? actionCount : 0;

  return {
    status: request.status,
    stage: statusMatrix.serviceDashboardBucket,
    stageLabel: orientation.stageLabel,
    nextActionOwner: orientation.nextActionOwner,
    orientation,
    nextActionType: getActionType(
      presentation.primaryAction,
      hasUnreadMessages,
      request.status,
      actor
    ),
    hasWorkflowAction,
    hasUnreadSignal,
    showActionUrgency,
    requiresAttention,
    attentionReason,
    primaryPanel,
    actionPanel,
    orderTone,
    availableActions: {
      primary: presentation.primaryAction,
      secondary: presentation.secondaryActions,
      destructive: presentation.destructiveActions,
      all: allActions,
    },
    unreadSections,
    headerBadges: [
      {
        id: 'status',
        label: presentation.statusLabel,
        variant: presentation.statusVariant,
      },
      {
        id: 'stage',
        label: `Etap: ${statusMatrix.stageLabel}`,
        variant: 'neutral',
      },
      ...(showActionUrgency
        ? [
            {
              id: 'attention',
              label: 'Do działania',
              variant: 'warning' as OrderCardBadgeVariant,
            },
          ]
        : []),
    ],
    summaryLine,
    actor,
    variant,
    communicationPriority: presentation.communicationPriority,
    hasUnreadMessages,
    hasFreshRequestActivity,
    header: {
      title: device.label,
      brandModelLabel: device.brandModelLabel,
      requestShortId: String(request.id || request.uid || '').slice(0, 6) || 'N/A',
      counterpartLabel,
      statusLabel: presentation.statusLabel,
      stageLabel: statusMatrix.stageLabel,
      issueSummary,
      identification,
    },
    rows: {
      description: {
        title: 'Opis zlecenia',
        subtitle: issueSummary,
        tone:
          statusMatrix.serviceDashboardTone === 'danger' && INTAKE_STATUSES.has(request.status)
            ? 'danger'
            : descriptionActionCount > 0
              ? 'warning'
              : 'neutral',
        hasAttention: descriptionActionCount > 0,
        section: 'description',
        actionCount: descriptionActionCount,
      },
      communication: {
        title:
          actor === 'service' ? 'Komunikacja z właścicielem zgłoszenia' : 'Komunikacja z serwisem',
        subtitle: getCommunicationSubtitle({
          communicationLabel: presentation.communicationLabel,
        }),
        tone:
          hasUnreadMessages || presentation.communicationPriority === 'attention'
            ? 'warning'
            : 'neutral',
        hasAttention: hasUnreadMessages,
        section: 'messages',
        hint:
          actor === 'service'
            ? 'Ustal szybkie decyzje z właścicielem zgłoszenia.'
            : 'Ustal szybkie decyzje z serwisem.',
      },
      settlement: {
        title: getSettlementTitle(request.status, workflowV15.isV15),
        subtitle:
          settlementActionCount > 0 && actionCue
            ? actionCue
            : getSettlementSubtitle({ status: request.status, actor }),
        tone:
          request.status === RepairStatus.COMPLETED
            ? 'success'
            : settlementActionCount > 0 || unreadSections.includes('quote')
              ? 'warning'
              : 'neutral',
        hasAttention: settlementActionCount > 0 || unreadSections.includes('quote'),
        section: 'settlement',
        actionCount: settlementActionCount,
      },
    },
  };
};
